import { CompositionConfig } from "../types/composition";
import {
    AudioSpectrumSchema,
    defaultAudioSpectrumProps,
    spectrumPresets,
    defaultSpectrumConfig,
} from "./constants";
import { MainComposition } from "./main";
import {
    PRESETS,
    MIN_DURATION_FRAMES,
    CROSSFADE_DURATION_FRAMES,
} from "../constants";
import { calculateTotalDuration } from "../utils/calculate-duration";

export { defaultSpectrumConfig };

export const AudioSpectrumTemplate: CompositionConfig<React.ComponentProps<typeof MainComposition>> = {
    id: "AudioSpectrum",
    component: MainComposition,
    schema: AudioSpectrumSchema,
    defaultProps: defaultAudioSpectrumProps,
    width: PRESETS.dimensions.landscape.width,
    height: PRESETS.dimensions.landscape.height,
    fps: PRESETS.fps,
    calculateMetadata: async ({ props }) => {
        const fps = PRESETS.fps;

        // Handle Preset Switching Logic
        // If the preset has changed from what we last tracked, overwrite values with new preset defaults
        const currentPreset = props.spectrum.preset;
        const previousPreset = props.spectrum.lastPreset;

        let newSpectrumProps = { ...props.spectrum };

        if (currentPreset !== previousPreset) {
            // Preset changed! Apply new defaults
            // Explicitly cast to any to key into spectrumPresets because TS might not narrow it perfectly
            const presetValues = (spectrumPresets as any)[currentPreset];
            if (presetValues) {
                newSpectrumProps = {
                    ...newSpectrumProps,
                    ...presetValues,
                    lastPreset: currentPreset,
                };
            }
        } else if (!previousPreset) {
            // Initialize lastPreset if missing
            newSpectrumProps.lastPreset = currentPreset;
        }

        const newProps = {
            ...props,
            spectrum: newSpectrumProps,
        };

        // Calculate total duration from provided durationInSeconds
        const totalDurationSeconds = calculateTotalDuration(newProps.audioTracks);

        // Account for crossfades between tracks
        const totalCrossfades = Math.max(0, newProps.audioTracks.length - 1);
        const crossfadeSeconds =
            (totalCrossfades * CROSSFADE_DURATION_FRAMES) / fps;
        const adjustedDuration = totalDurationSeconds - crossfadeSeconds;

        return {
            durationInFrames: Math.ceil(
                Math.max(MIN_DURATION_FRAMES, adjustedDuration * fps),
            ),
            props: newProps,
        };
    },
};
